import React from 'react';
import {
    ArrowRight,
    Building2,
    Users,
    FileText,
    Lock,
    AlertCircle,
    CheckCircle2,
    XCircle,
    Clock,
    RefreshCw,
    ExternalLink,
    ChevronRight,
    Search,
    AlertTriangle,
    ShieldAlert,
    Calendar,
    Brain,
} from 'lucide-react';




/* ─────────────────────────────────────────────────────────
   Types
 ───────────────────────────────────────────────────────── */
interface QuickReportPageProps {
    nip: string;
    onCheckNip: (nip: string) => void;
}

type LoadState = 'idle' | 'loading' | 'success' | 'error' | 'not_found' | 'rate_limited';

interface CompanyData {
    name: string;
    nip: string;
    regon: string;
    krs: string;
    isFoundation: boolean;
    legalForm: string;
    status: 'active' | 'suspended' | 'struck_off' | 'liquidation' | 'bankruptcy' | 'unknown';
    statusRaw: string;          // raw companyVerResults.status
    verResult: 'POSITIV' | 'NEGATIV' | string;
    verMessage: string;
    stanNaDzien: string;
    seat: string;
    organName: string;
    organRepresentatives: Representative[];
    procurators: Representative[];
    representationRule: string;
}

interface Representative {
    name: string;
    role: string;
    peselMasked: string;
}

/* ─────────────────────────────────────────────────────────
   Mock data generator (deterministic from NIP)
 ───────────────────────────────────────────────────────── */
function mapApiResponse(apiResponse: any, requestedId: string): CompanyData {
    const krsData = apiResponse.krsData || {};
    const danePodmiotu = krsData.danePodmiotu || {};
    
    // Robust NIP extraction
    let nipFromData = (apiResponse.nip || danePodmiotu["Numer NIP"] || '').trim();
    if (!nipFromData || !isValidIdentifier(nipFromData)) {
        const nipMatch = (danePodmiotu["Numer REGON/NIP"] || '').match(/NIP: ([\d]+)/);
        if (nipMatch) nipFromData = nipMatch[1];
    }

    const regonMatch = (danePodmiotu["Numer REGON/NIP"] || '').match(/REGON: ([\d]+)/);
    const legalForm = danePodmiotu["Oznaczenie formy prawnej"] || 'Brak danych';
    
    // Check if we have explicit isFoundation from SEO data or API
    const seoData = (typeof window !== 'undefined') ? (window as any).__INITIAL_SEO_DATA__ : null;
    const isFoundation = (seoData && seoData.isFoundation !== undefined) 
        ? seoData.isFoundation 
        : (krsData.rejestr === 'S' || (requestedId && requestedId.startsWith('0') && cleanId(requestedId).length === 10));

    const krsNumber = cleanId(apiResponse.krs || krsData.krs || (danePodmiotu["Numer KRS"] || ''));
    const finalKrs = krsNumber || (requestedId && cleanId(requestedId).length === 10 ? cleanId(requestedId) : 'Brak');
    const finalNip = (nipFromData && isValidIdentifier(nipFromData)) ? cleanId(nipFromData) : (!isFoundation ? cleanId(requestedId) : '');

    const verResult = apiResponse.companyVerResults?.result || 'POSITIV';
    const verMessage = apiResponse.companyVerResults?.message || '';
    const statusRaw = apiResponse.companyVerResults?.status || 'AKTYWNY';

    // Map to typed status
    let status: CompanyData['status'] = 'active';
    const sRaw = statusRaw.toUpperCase();
    if (sRaw.includes('ZAWIESZON')) status = 'suspended';
    else if (sRaw.includes('WYKREŚLON') || sRaw.includes('WYKRESLONY')) status = 'struck_off';
    else if (sRaw.includes('LIKWIDACJ')) status = 'liquidation';
    else if (sRaw.includes('UPADŁOŚĆ') || sRaw.includes('UPADLOSC') || sRaw.includes('UPADŁOŚ')) status = 'bankruptcy';
    else if (sRaw === 'BRAK') status = 'unknown';

    const organRepresentatives: Representative[] = [];

    if (status === 'bankruptcy' && krsData.upadlosc?.syndycy?.length > 0) {
        krsData.upadlosc.syndycy.forEach((syn: any) => {
            const name = `${syn["Imiona"] || ''} ${syn["Nazwisko"] || ''}`.trim();
            organRepresentatives.push({
                name: name || 'Nieznany',
                role: 'Syndyk',
                peselMasked: syn["PESEL/Data ur."] || '****',
            });
        });
    } else if (krsData.reprezentacja?.sklad) {
        krsData.reprezentacja.sklad.forEach((czlonek: any) => {
            const name = czlonek["Nazwa"] || `${czlonek["Imiona"] || ''} ${czlonek["Nazwisko"] || ''}`.trim();
            organRepresentatives.push({
                name: name || 'Nieznany',
                role: czlonek["Funkcja"] || 'Członek organu',
                peselMasked: czlonek["PESEL/REGON/Data ur."] || czlonek["Numer KRS"] || '****',
            });
        });
    }

    if (status === 'liquidation' && krsData.likwidacja?.likwidatorzy?.length > 0 && organRepresentatives.length === 0) {
        krsData.likwidacja.likwidatorzy.forEach((lik: any) => {
            const name = `${lik["Imiona"] || ''} ${lik["Nazwisko"] || ''}`.trim();
            organRepresentatives.push({
                name: name || 'Nieznany',
                role: 'Likwidator',
                peselMasked: lik["PESEL/Data ur."] || '****',
            });
        });
    }

    const procurators: Representative[] = [];
    if (krsData.prokurenci) {
        krsData.prokurenci.forEach((prok: any) => {
            const name = `${prok["Imiona"] || ''} ${prok["Nazwisko"] || ''}`.trim();
            procurators.push({
                name: name || 'Nieznany',
                role: `Prokurent (${prok["Rodzaj prokury"] || ''})`.trim(),
                peselMasked: prok["PESEL/Data ur."] || '****',
            });
        });
    }

    const representationRule = status === 'bankruptcy'
        ? krsData.upadlosc?.sposobProwadzeniaPostepowaniaUpadlosciowego || 'Brak danych'
        : status === 'liquidation'
            ? krsData.likwidacja?.sposobReprezentacjiLikwidatorzy || 'Brak danych'
            : krsData.reprezentacja?.sposobReprezentacji || 'Brak danych';

    return {
        name: danePodmiotu["Firma, pod którą spółka działa"] || `Podmiot (${isFoundation ? 'KRS' : 'NIP'} ${requestedId})`,
        nip: finalNip,
        regon: regonMatch ? regonMatch[1] : 'Brak',
        krs: finalKrs,
        isFoundation,
        legalForm,
        status,
        statusRaw,
        verResult,
        verMessage,
        stanNaDzien: krsData.stanNaDzien || '',
        seat: apiResponse.adres || apiResponse.Adres || krsData.adres || krsData.Adres || danePodmiotu?.Adres || 'Brak danych',
        organName: status === 'bankruptcy'
            ? 'Syndycy'
            : status === 'liquidation'
                ? 'Likwidatorzy'
                : krsData.reprezentacja?.nazwaOrganu || 'Organ uprawniony do reprezentacji',
        organRepresentatives,
        procurators,
        representationRule,
    };
}

function isValidIdentifier(val: string): boolean {
    return /^\d{10}$/.test(val.replace(/[^\d]/g, ''));
}

const cleanId = (val: string) => (val || '').replace(/[^\d]/g, '');

function StatusBadge({ status, statusRaw }: { status: CompanyData['status']; statusRaw: string }) {
    const config: Record<CompanyData['status'], { label: string; cls: string; icon: React.ReactNode }> = {
        active: { label: 'Aktywny', cls: 'bg-emerald-100 text-emerald-700 border border-emerald-300', icon: <CheckCircle2 className="w-4 h-4" /> },
        suspended: { label: 'Zawieszony', cls: 'bg-amber-100 text-amber-700 border border-amber-300', icon: <Clock className="w-4 h-4" /> },
        struck_off: { label: 'Wykreślony', cls: 'bg-red-100 text-red-700 border border-red-300', icon: <XCircle className="w-4 h-4" /> },
        liquidation: { label: 'W likwidacji', cls: 'bg-orange-100 text-orange-700 border border-orange-300', icon: <AlertTriangle className="w-4 h-4" /> },
        bankruptcy: { label: 'W upadłości', cls: 'bg-red-100 text-red-800 border border-red-400', icon: <ShieldAlert className="w-4 h-4" /> },
        unknown: { label: statusRaw || 'Nieznany', cls: 'bg-slate-100 text-slate-600 border border-slate-300', icon: <AlertCircle className="w-4 h-4" /> },
    };

    const { label, cls, icon } = config[status];
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${cls}`}>
            {icon}
            {label}
        </span>
    );
}

/* ─────────────────────────────────────────────────────────
   Skeleton
 ───────────────────────────────────────────────────────── */
function SkeletonCard({ lines = 4 }: { lines?: number }) {
    return (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm animate-pulse">
            <div className="h-5 bg-slate-200 rounded w-2/5 mb-6" />
            {Array.from({ length: lines }).map((_, i) => (
                <div key={i} className="space-y-2 mb-3">
                    <div className="h-3 bg-slate-100 rounded w-1/3" />
                    <div className="h-4 bg-slate-200 rounded w-3/4" />
                </div>
            ))}
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   NIP Form (re-entry)
 ───────────────────────────────────────────────────────── */
function NipForm({
    initialNip,
    onSubmit,
    loading,
}: {
    initialNip: string;
    onSubmit: (nip: string) => void;
    loading: boolean;
}) {
    const [value, setValue] = React.useState(initialNip);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        setValue(initialNip);
    }, [initialNip]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const cleaned = cleanId(value);
        if (!isValidIdentifier(cleaned)) {
            setError('Identyfikator musi zawierać dokładnie 10 cyfr.');
            return;
        }
        setError('');
        onSubmit(cleaned);
    };

    return (
        <form onSubmit={handleSubmit} className="w-full">
            <div className="flex flex-col sm:flex-row gap-3 items-start">
                <div className="flex-1 w-full">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                        <input
                            id="report-nip-input"
                            type="text"
                            inputMode="numeric"
                            value={value}
                            onChange={(e) => { setValue(e.target.value); setError(''); }}
                            placeholder="Numer NIP lub KRS..."
                            maxLength={13}
                            disabled={loading}
                            className="w-full pl-10 pr-4 h-12 rounded-lg border border-slate-300 bg-white text-slate-800 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent disabled:opacity-60 transition"
                        />
                    </div>
                    {error && <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{error}</p>}
                </div>
                <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center gap-2 h-12 px-7 rounded-lg bg-accent text-primary font-bold text-sm hover:bg-accent-hover transition-all disabled:opacity-60 whitespace-nowrap shrink-0 shadow-[0_0_15px_rgba(0,225,173,0.25)] hover:shadow-[0_0_20px_rgba(0,225,173,0.4)]"
                >
                    {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Sprawdź'}
                    {!loading && <ChevronRight className="w-4 h-4" />}
                </button>
            </div>
        </form>
    );
}

/* ─────────────────────────────────────────────────────────
   Upgrade CTA Banner
 ───────────────────────────────────────────────────────── */
function UpgradeBanner() {
    return (
        <div className="relative overflow-hidden rounded-2xl bg-primary text-white p-8 md:p-10 mt-10">
            {/* Decorative glow */}
            <div className="pointer-events-none absolute -top-20 -right-20 w-64 h-64 bg-accent/10 rounded-full blur-3xl" />
            <div className="pointer-events-none absolute -bottom-10 -left-10 w-48 h-48 bg-accent/5 rounded-full blur-2xl" />

            <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
                <div className="flex-1">
                    <h3 className="text-xl font-bold font-headline mb-2">
                        To był <span className="text-accent">podgląd raportu</span> Verifiko
                    </h3>
                    <p className="text-white font-semibold text-sm mb-3">
                        Nie jesteś prawnikiem? Nie szkodzi. Sprawdzimy czy wskazana reprezentacja jest uprawniona w tej chwili do czynności w imieniu spółki.
                    </p>
                    <p className="text-slate-400 text-sm leading-relaxed max-w-xl">
                        Zaloguj się lub zarejestruj, aby uzyskać pełen raport z weryfikacją sposobu
                        reprezentacji, historią zapytań i możliwością pobrania PDF.
                    </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                    <a
                        href={`${(import.meta as any).env.VITE_APP_URL || '__VITE_APP_URL_PLACEHOLDER__'}/register`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-lg bg-accent text-primary font-bold text-sm hover:bg-accent-hover transition-all shadow-[0_0_15px_rgba(0,225,173,0.3)] hover:shadow-[0_0_25px_rgba(0,225,173,0.5)] whitespace-nowrap"
                    >
                        Zarejestruj się za darmo
                        <ArrowRight className="w-4 h-4" />
                    </a>
                    <a
                        href={(import.meta as any).env.VITE_APP_URL || '__VITE_APP_URL_PLACEHOLDER__'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-lg border border-white/20 bg-transparent text-white hover:bg-white/10 font-medium text-sm transition-colors whitespace-nowrap"
                    >
                        Zaloguj się
                        <ExternalLink className="w-4 h-4" />
                    </a>
                </div>
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   Locked CTA Box
 ───────────────────────────────────────────────────────── */
function LockedCTA({ title, description }: { title: string; description: string }) {
    return (
        <div className="mt-4 flex flex-col items-center gap-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 py-8 px-4 text-center">
            <div className="bg-primary/5 p-3 rounded-full">
                <Lock className="w-6 h-6 text-slate-400" />
            </div>
            <div>
                <p className="text-sm font-semibold text-slate-700 mb-1">{title}</p>
                <p className="text-xs text-slate-500 max-w-xs">{description}</p>
            </div>
            <a
                href={`${(import.meta as any).env.VITE_APP_URL || '__VITE_APP_URL_PLACEHOLDER__'}/register`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-accent text-primary font-bold text-sm hover:bg-accent-hover transition-all"
            >
                Wypróbuj bezpłatnie
                <ArrowRight className="w-4 h-4" />
            </a>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   Automatyczna Analiza Reprezentacji CTA
 ───────────────────────────────────────────────────────── */
function AILockedCTA({ date, nip }: { date?: string; nip?: string }) {
    const seoData = (window as any).__INITIAL_SEO_DATA__;
    const hasSeoText = seoData && seoData.nip === nip && seoData.analysis;
    const isStale = (window as any).__VERIFIKO_STALE_ANALYSIS__;

    if (!hasSeoText || isStale) return null;

    const dateStr = date ? ` na dzień ${date}` : '';
    return (
        <section className="ai-sample-analysis mt-8 flex flex-col items-center gap-4 rounded-xl border border-dashed border-accent/50 bg-accent/5 py-8 px-6 text-center">
            <div className="bg-white p-3 rounded-full shadow-sm border border-slate-100">
                <Brain className="w-6 h-6 text-accent" />
            </div>
            <div>
                <h3 className="text-lg font-bold text-primary mb-3">Automatyczna Analiza Reprezentacji</h3>
                <p className="text-sm text-slate-700 max-w-2xl mx-auto leading-relaxed mb-4 text-left font-medium p-4 bg-white/50 rounded-lg border border-slate-200">
                    {seoData.analysis}
                </p>
                <small className="text-slate-500 max-w-lg block mx-auto leading-relaxed mt-2">
                    Analiza automatyczna{dateStr}. Aby zweryfikować bieżący stan w czasie rzeczywistym, odśwież zapytanie poniżej.
                </small>
            </div>
            <a
                href={`/sprawdz-firme?nip=${nip}`}
                className="mt-2 inline-flex items-center gap-2 h-11 px-6 rounded-lg bg-accent text-primary font-bold text-sm hover:bg-accent-hover transition-all shadow-[0_0_15px_rgba(0,225,173,0.3)] hover:shadow-[0_0_20px_rgba(0,225,173,0.4)]"
            >
                Sprawdź aktualny stan
                <ArrowRight className="w-4 h-4" />
            </a>
        </section>
    );
}

function Breadcrumbs({ name }: { name?: string }) {
    return (
        <nav className="flex items-center gap-2 text-xs text-slate-400 mb-6 font-medium">
            <a href="/" className="hover:text-accent transition-colors">Strona Główna</a>
            <ChevronRight className="w-3 h-3" />
            <a href="/krs" className="hover:text-accent transition-colors">KRS</a>
            {name && (
                <>
                    <ChevronRight className="w-3 h-3" />
                    <span className="text-slate-600 truncate max-w-[200px]">{name}</span>
                </>
            )}
        </nav>
    );
}

function KrsDirectory({ currentPath }: { currentPath: string }) {
    const isOnFirmy = currentPath === '/krs/firmy' || currentPath === '/krs/firmy/';
    const isOnFundacje = currentPath === '/krs/fundacje' || currentPath === '/krs/fundacje/';
    const isOnList = isOnFirmy || isOnFundacje;

    // When on a list subpage, show a context banner (actual list content is in static HTML,
    // served from dist/ in production — React hydration takes over only for /krs/{slug} pages)
    if (isOnList) {
        const listLabel = isOnFirmy ? 'Top 500 Firm w Polsce' : 'Top 500 Fundacji i OPP';
        const listDesc = isOnFirmy
            ? 'Największe spółki akcyjne i z o.o. zarejestrowane w KRS z danymi o sposobie reprezentacji.'
            : 'Fundacje i organizacje OPP zarejestrowane w KRS z danymi o sposobie reprezentacji.';

        return (
            <div className="mt-8 border-t border-slate-200 pt-10">
                <div className="flex items-start gap-4 p-6 bg-slate-50 border border-slate-200 rounded-2xl mb-8">
                    <div className="bg-accent/10 p-3 rounded-xl shrink-0">
                        <span className="text-2xl">{isOnFirmy ? '🏢' : '🤝'}</span>
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-primary mb-1">{listLabel}</h2>
                        <p className="text-sm text-slate-500 mb-4">{listDesc}</p>
                        <p className="text-xs text-slate-400 mb-3">
                            Pełna lista podmiotów jest dostępna po zbudowaniu projektu (<code className="bg-slate-100 px-1 py-0.5 rounded">npm run build</code>)
                            i jest serwowana jako statyczny HTML z wygenerowanymi linkami do profili firm.
                        </p>
                        <div className="flex items-center gap-3">
                            <a href="/krs" className="text-sm text-accent font-bold hover:underline">
                                ← Powrót do katalogu KRS
                            </a>
                            <span className="text-slate-300">|</span>
                            <a href="/sprawdz-firme" className="text-sm text-slate-500 hover:text-accent">
                                Sprawdź firmę po NIP →
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Hub view (/krs): show two category cards
    return (
        <div className="mt-8 border-t border-slate-200 pt-10">
            <p className="text-slate-500 mb-8 font-medium">
                Poniżej znajdziesz katalog podmiotów z danymi KRS o sposobie reprezentacji i statusie prawnym.
                Kliknij kategorię, aby przejść do pełnej listy.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <a
                    href="/krs/firmy"
                    className="group flex flex-col gap-3 p-7 rounded-2xl border border-slate-200 bg-white hover:border-accent hover:shadow-md transition-all"
                >
                    <div className="flex items-center gap-3">
                        <div className="bg-accent/10 p-3 rounded-xl">
                            <span className="text-2xl">🏢</span>
                        </div>
                        <h2 className="text-lg font-bold text-primary group-hover:text-accent transition-colors">
                            Top 500 Firm w Polsce
                        </h2>
                    </div>
                    <p className="text-sm text-slate-500 leading-relaxed">
                        Największe spółki akcyjne i spółki z o.o. zarejestrowane w KRS.
                        Dane o sposobie reprezentacji, składzie zarządu i statusie prawnym.
                    </p>
                    <span className="text-accent font-bold text-sm self-start mt-1 group-hover:underline">
                        Przeglądaj listę →
                    </span>
                </a>

                <a
                    href="/krs/fundacje"
                    className="group flex flex-col gap-3 p-7 rounded-2xl border border-slate-200 bg-white hover:border-accent hover:shadow-md transition-all"
                >
                    <div className="flex items-center gap-3">
                        <div className="bg-accent/10 p-3 rounded-xl">
                            <span className="text-2xl">🤝</span>
                        </div>
                        <h2 className="text-lg font-bold text-primary group-hover:text-accent transition-colors">
                            Top 500 Fundacji i OPP
                        </h2>
                    </div>
                    <p className="text-sm text-slate-500 leading-relaxed">
                        Fundacje i organizacje pożytku publicznego zarejestrowane w KRS.
                        Sprawdź kto reprezentuje największe organizacje w Polsce.
                    </p>
                    <span className="text-accent font-bold text-sm self-start mt-1 group-hover:underline">
                        Przeglądaj listę →
                    </span>
                </a>
            </div>
        </div>
    );
}


/* ─────────────────────────────────────────────────────────
   Report Cards
 ───────────────────────────────────────────────────────── */
function CompanyStatusCard({ data }: { data: CompanyData }) {
    const rows = [
        { label: 'Nazwa', value: data.name },
        { label: 'Forma prawna', value: data.legalForm },
        { label: 'NIP', value: (data.nip && data.krs && cleanId(data.nip) === cleanId(data.krs)) ? null : data.nip },
        { label: 'REGON', value: data.regon },
        { label: 'KRS', value: data.krs },
        { label: 'Siedziba', value: data.seat },
    ].filter(row => row.value && row.value !== 'Brak' && row.value !== 'Brak danych');

    const isNegativ = data.verResult === 'NEGATIV';

    return (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden">
            {/* Card header */}
            <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100 bg-slate-50">
                <div className="bg-accent/10 p-2 rounded-lg">
                    <Building2 className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-bold text-primary text-base">Status Podmiotu</h3>
            </div>

            {/* Content */}
            <div className="px-6 py-5 space-y-4">
                {/* Status badge only */}
                <div>
                    <StatusBadge status={data.status} statusRaw={data.statusRaw} />
                </div>

                {/* Data rows */}
                <div className="grid grid-cols-1 gap-3">
                    {rows.map(({ label, value }) => (
                        <div key={label} className="flex flex-col gap-0.5">
                            <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">{label}</span>
                            <span className="text-sm text-slate-800 font-medium">{value}</span>
                        </div>
                    ))}
                </div>

                {/* Active locked box */}
                {data.status === 'active' && (
                    <LockedCTA
                        title="Szersza informacja o firmie"
                        description="Kapitał, przedmiot działalności.. Dostępne dla zarejestrowanych użytkowników."
                    />
                )}

                {/* NEGATIV locked box at the end */}
                {isNegativ && (
                    <LockedCTA
                        title="Pełna informacja o organach reprezentacji"
                        description="Szczegóły reprezentacji i informacje o otwarciu postępowań dostępne dla zarejestrowanych użytkowników."
                    />
                )}
            </div>
        </div>
    );
}

function RepresentativesCard({ data }: { data: CompanyData }) {
    return (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100 bg-slate-50">
                <div className="bg-accent/10 p-2 rounded-lg">
                    <Users className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-bold text-primary text-base">Reprezentacja</h3>
            </div>

            <div className="relative px-6 py-5 space-y-6">
                {/* Organ Section */}
                <div>
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">{data.organName}</h4>
                    <div className="space-y-4">
                        {data.organRepresentatives.length > 0 ? (
                            data.organRepresentatives.map((rep, i) => (
                                <div key={i} className="flex flex-col gap-0.5">
                                    <span className="text-sm font-semibold text-slate-800">{rep.name}</span>
                                    <span className="text-xs text-slate-500">{rep.role}</span>
                                    <span className="text-xs text-slate-400 font-mono">PESEL/KRS: {rep.peselMasked}</span>
                                </div>
                            ))
                        ) : (
                            <p className="text-xs text-slate-400">Brak danych o organie.</p>
                        )}
                    </div>
                </div>

                {/* Procurators Section */}
                {data.procurators.length > 0 && (
                    <div>
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Prokurenci</h4>
                        <div className="space-y-4">
                            {data.procurators.map((rep, i) => (
                                <div key={i} className="flex flex-col gap-0.5">
                                    <span className="text-sm font-semibold text-slate-800">{rep.name}</span>
                                    <span className="text-xs text-slate-500">{rep.role}</span>
                                    <span className="text-xs text-slate-400 font-mono">PESEL/KRS: {rep.peselMasked}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Locked CTA */}
                <LockedCTA
                    title="Pełna weryfikacja osób"
                    description="Zweryfikuj tożsamość osób włącznie z PESEL. Dostępne dla zarejestrowanych użytkowników."
                />
            </div>
        </div>
    );
}

function RepresentationCard({ data }: { data: CompanyData }) {
    return (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100 bg-slate-50">
                <div className="bg-accent/10 p-2 rounded-lg">
                    <FileText className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-bold text-primary text-base">Sposób Reprezentacji</h3>
            </div>

            <div className="px-6 py-5">
                {/* Teaser text */}
                <p className="text-sm text-slate-700 font-medium leading-relaxed mb-5">
                    {data.representationRule}
                </p>

                {/* Locked CTA */}
                <LockedCTA
                    title="Weryfikacja sposobu reprezentacji"
                    description="Weryfikacja wskazanych reprezentantów względem zasad reprezentacji dostępne dla zarejestrowanych użytkowników."
                />
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   Main component
 ───────────────────────────────────────────────────────── */
const QuickReportPage: React.FC<QuickReportPageProps> = ({ nip, onCheckNip }) => {
    const isClient = typeof window !== 'undefined';
    const pathname = isClient ? window.location.pathname : '/';
    const isDirectory = isClient && (
        pathname === '/krs' || pathname === '/krs/' ||
        pathname === '/krs/firmy' || pathname === '/krs/firmy/' ||
        pathname === '/krs/fundacje' || pathname === '/krs/fundacje/'
    );
    const seoData = isClient ? (window as any).__INITIAL_SEO_DATA__ : null;
    // isFirmPage = any /krs/{slug} path that is NOT one of the list/hub pages
    const isFirmPage = isClient && pathname.startsWith('/krs/') && !isDirectory;

    const [loadState, setLoadState] = React.useState<LoadState>(() => {
        if (!seoData || !isFirmPage) return 'idle';
        // Bold identification: check if current nip search matches seo metadata (either nip or krs)
        const cleanReqNip = cleanId(nip);
        const isMatch = (seoData.nip && cleanId(seoData.nip) === cleanReqNip) || 
                        (seoData.krs && cleanId(seoData.krs) === cleanReqNip) ||
                        (pathname.includes(cleanId(seoData.krs || '')));
        return isMatch ? 'success' : 'idle';
    });
    const [currentNip, setCurrentNip] = React.useState(nip);
    const [data, setData] = React.useState<CompanyData | null>(() => {
        if (!seoData || !isFirmPage || !seoData.fullData) return null;
        const cleanReqNip = cleanId(nip);
        const isMatch = (seoData.nip && cleanId(seoData.nip) === cleanReqNip) || 
                        (seoData.krs && cleanId(seoData.krs) === cleanReqNip) ||
                        (pathname.includes(cleanId(seoData.krs || '')));
        return isMatch ? mapApiResponse(seoData.fullData, seoData.nip || seoData.krs || cleanReqNip) : null;
    });
    const [isLive, setIsLive] = React.useState(false);
    const isFetchingRef = React.useRef(false);

    // Track usage count in sessionStorage (rate limiting placeholder)
    const USAGE_KEY = 'vf_web_usage';

    // Get max free reports from env var, default to 100 for testing
    const MAX_FREE = parseInt((import.meta as any).env.VITE_MAX_FREE_REPORTS || '100', 10);

    const getUsage = () => parseInt(localStorage.getItem(USAGE_KEY) || '0', 10);
    const incrementUsage = () => localStorage.setItem(USAGE_KEY, String(getUsage() + 1));

    const runCheck = React.useCallback(async (nipValue: string) => {
        if (!nipValue || isFetchingRef.current) return;

        isFetchingRef.current = true;
        const cleanNipValue = cleanId(nipValue);

        if (getUsage() >= MAX_FREE) {
            setLoadState('rate_limited');
            isFetchingRef.current = false;
            return;
        }

        setCurrentNip(nipValue);
        setLoadState('loading');
        setData(null);

        try {
            const apiUrl = (import.meta as any).env.VITE_API_URL || '__VITE_API_URL_PLACEHOLDER__';
            const apiKey = (import.meta as any).env.VITE_API_KEY || '__VITE_API_KEY_PLACEHOLDER__';

            const isKrs = cleanNipValue.startsWith('0') && cleanNipValue.length === 10;
            const queryParam = isKrs ? 'krs' : 'nip';

            const response = await fetch(`${apiUrl}/get-krs-data?${queryParam}=${cleanNipValue}`, {
                headers: {
                    ...(apiKey ? { 'X-Api-Key': apiKey } : {}),
                    'Content-Type': 'application/json',
                }
            });

            if (response.status === 429) {
                setLoadState('rate_limited');
                return;
            }

            const json = await response.json();

            if (json.companyVerResults?.result === 'NEGATIV' && json.companyVerResults?.status === 'BRAK') {
                setLoadState('not_found');
                return;
            }
            if (json.status === 'ERROR') {
                setLoadState('error');
                return;
            }
            if (!response.ok) {
                setLoadState('error');
                return;
            }

            incrementUsage();
            const freshData = mapApiResponse(json, nipValue);
            setData(freshData);
            setLoadState('success');
            setIsLive(true);

            // Check if AI analysis is stale
            if (seoData && seoData.nip === nipValue && seoData.stanNaDzien) {
                if (freshData.stanNaDzien !== seoData.stanNaDzien) {
                    (window as any).__VERIFIKO_STALE_ANALYSIS__ = true;
                }
            }
        } catch (error) {
            console.error('Failed to fetch data:', error);
            setLoadState('error');
        } finally {
            isFetchingRef.current = false;
        }
    }, []);

    // Run on initial mount with the NIP passed from hero
    React.useEffect(() => {
        if (nip) {
            // If we have hydrated from SEO data for this exact NIP/KRS, don't run check immediately
            const cleanReqNip = cleanId(nip);
            const isMatch = (seoData?.nip && cleanId(seoData.nip) === cleanReqNip) || 
                            (seoData?.krs && cleanId(seoData.krs) === cleanReqNip) ||
                            (pathname.includes(cleanId(seoData?.krs || '')));
            const alreadyHydrated = isMatch && isFirmPage;

            if (alreadyHydrated && (loadState === 'success' || data)) {
                return;
            }
            runCheck(nip);
        } else {
            // Only reset if we are NOT on a static SEO page
            // (static pages without NIP rely on hydration via KRS)
            if (!isFirmPage) {
                setLoadState('idle');
                setData(null);
                setCurrentNip('');
            }
        }
    }, [nip, pathname]);

    // Update document title
    React.useEffect(() => {
        // If we are on a static /krs/ page, we don't want to reset the title 
        // to a generic 'Sprawdź Firmę' unless there's a specific live search result.
        if (isFirmPage && data) {
            const hasBoth = data.nip && data.krs && cleanId(data.nip) !== cleanId(data.krs);
            const idLabel = (data.isFoundation || !data.nip || !hasBoth) ? `KRS ${data.krs}` : `NIP ${data.nip}`;
            document.title = `Kto reprezentuje ${data.name} (${idLabel})? Sposób reprezentacji | Verifiko`;
        } else {
            document.title = `Sprawdź Firmę${currentNip ? ` – ${currentNip}` : ''} – Verifiko`;
        }
        return () => { document.title = 'Verifiko - Szybka i Niezawodna Weryfikacja dla Finansów'; };
    }, [currentNip, data, isFirmPage]);

    const handleNewNip = (newNip: string) => {
        const isSameNip = newNip === currentNip;
        // Update URL without full navigation
        if (isClient) {
            const isKrs = cleanId(newNip).startsWith('0') && cleanId(newNip).length === 10;
            window.history.pushState({ view: 'report', nip: newNip }, '', `/sprawdz-firme?${isKrs ? 'krs' : 'nip'}=${newNip}`);
        }
        onCheckNip(newNip);

        // If it's the same NIP, the useEffect won't trigger, so we call it manually.
        // If it's a different NIP, the useEffect reacting to prop change will handle it.
        if (isSameNip) {
            runCheck(newNip);
        }
    };

    const isLoading = loadState === 'loading';

    return (
        <div className="min-h-screen bg-slate-50">


            {/* ── Page body ── */}
            <div className="w-4/5 mx-auto px-4 md:px-6 py-10 md:py-14">

                {isFirmPage && <Breadcrumbs name={data?.name} />}

                {/* Page title + NIP form */}
                <div className="mb-10">
                    <h1 className="text-3xl md:text-4xl font-extrabold font-headline text-primary mb-2">
                        {isFirmPage && data
                            ? `${data.name} – Sposób Reprezentacji | KRS ${data.krs}`
                            : (isClient && pathname.startsWith('/krs/firmy'))
                                ? 'Top 500 Firm w Polsce – Dane KRS'
                                : (isClient && pathname.startsWith('/krs/fundacje'))
                                    ? 'Top 500 Fundacji i OPP – Dane KRS'
                                    : (isClient && pathname.startsWith('/krs'))
                                        ? 'Katalog KRS Verifiko'
                                        : 'Sprawdź Spółkę'}
                    </h1>
                    <p className="text-slate-500 mb-7">
                        {isFirmPage && data
                            ? `Podgląd danych z Krajowego Rejestru Sądowego dla ${data.nip ? `NIP ${data.nip}` : `KRS ${data.krs}`}.`
                            : (isClient && pathname.startsWith('/krs'))
                                ? 'Lista podmiotów z dostępnymi raportami o sposobie reprezentacji i statusie w rejestrze sądowym.'
                                : 'Podgląd danych z Krajowego Rejestru Sądowego bez logowania. Sprawdź bezpłatnie.'}
                    </p>
                    <NipForm initialNip={currentNip} onSubmit={handleNewNip} loading={isLoading} />
                </div>

                {/* Directory links when on any /krs hub/list path and not showing a specific firm */}
                {!isFirmPage && isClient && pathname.startsWith('/krs') && (
                    <div className="mb-12">
                        <KrsDirectory currentPath={pathname} />
                    </div>
                )}

                {/* ── Loading ── */}
                {isLoading && !data && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <SkeletonCard lines={6} />
                        <SkeletonCard lines={5} />
                        <SkeletonCard lines={4} />
                    </div>
                )}

                {/* ── Success ── */}
                {(loadState === 'success' || (isLoading && data)) && data && (
                    <>
                        {/* Shared dataset date - ONLY SHOW IF LIVE OR IF USER SAYS OK TO SSG DATA (user said: hide until live) */}
                        {isLive && data.stanNaDzien && (
                            <div className="flex items-center gap-2 mb-5 text-xs text-slate-500">
                                <Calendar className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                                <span>
                                    Stan na dzień:{' '}
                                    <span className="font-semibold text-slate-700">{data.stanNaDzien}</span>
                                    {' '}
                                    <span className="text-slate-400">(ostatni stan w KRS)</span>
                                </span>
                            </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <CompanyStatusCard data={data} />
                            <RepresentativesCard data={data} />
                            <RepresentationCard data={data} />
                        </div>
                        <AILockedCTA date={data.stanNaDzien} nip={data.nip} />
                        <UpgradeBanner />
                    </>
                )}

                {/* ── Not found ── */}
                {loadState === 'not_found' && (
                    <div className="flex flex-col items-center text-center py-16 gap-5">
                        <div className="bg-slate-100 p-5 rounded-full">
                            <AlertCircle className="w-10 h-10 text-slate-400" />
                        </div>
                        <h2 className="text-xl font-bold text-primary">Nie znaleziono podmiotu</h2>
                        <p className="text-slate-500 max-w-sm">
                            Upewnij się, że NIP jest poprawny i spróbuj ponownie.
                        </p>
                        <button
                            onClick={() => runCheck(currentNip)}
                            className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-accent text-primary font-bold text-sm hover:bg-accent-hover transition-all"
                        >
                            <RefreshCw className="w-4 h-4" /> Spróbuj ponownie
                        </button>
                    </div>
                )}

                {/* ── Generic error ── */}
                {loadState === 'error' && (
                    <div className="flex flex-col items-center text-center py-16 gap-5">
                        <div className="bg-red-50 p-5 rounded-full">
                            <XCircle className="w-10 h-10 text-red-400" />
                        </div>
                        <h2 className="text-xl font-bold text-primary">Wystąpił błąd</h2>
                        <p className="text-slate-500 max-w-sm">
                            Nie udało się pobrać danych. Spróbuj ponownie za chwilę.
                        </p>
                        <button
                            onClick={() => runCheck(currentNip)}
                            className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-accent text-primary font-bold text-sm hover:bg-accent-hover transition-all"
                        >
                            <RefreshCw className="w-4 h-4" /> Ponów zapytanie
                        </button>
                    </div>
                )}

                {/* ── Rate limited ── */}
                {loadState === 'rate_limited' && (
                    <div className="flex flex-col items-center text-center py-16 gap-5">
                        <div className="bg-amber-50 p-5 rounded-full">
                            <Lock className="w-10 h-10 text-amber-400" />
                        </div>
                        <h2 className="text-xl font-bold text-primary">Wyczerpałeś darmowy limit</h2>
                        <p className="text-slate-500 max-w-sm">
                            Skorzystałeś z maksymalnej liczby bezpłatnych podglądów. Zarejestruj się za darmo,
                            aby weryfikować bez limitu.
                        </p>
                        <a
                            href={`${(import.meta as any).env.VITE_APP_URL || '__VITE_APP_URL_PLACEHOLDER__'}/register`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 h-11 px-6 rounded-lg bg-accent text-primary font-bold text-sm hover:bg-accent-hover transition-all"
                        >
                            Zarejestruj się bezpłatnie <ArrowRight className="w-4 h-4" />
                        </a>
                    </div>
                )}

                {/* SEO: keyword-rich static section - ALWAYS VISIBLE */}
                <section className="mt-12 border-t border-slate-200 pt-10">
                    <h2 className="text-xl font-bold text-primary font-headline mb-4">Jak to działa?</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-slate-600 leading-relaxed">
                        <div>
                            <h3 className="font-semibold text-primary mb-1">Dane z KRS w kilka sekund</h3>
                            <p>
                                Verifiko łączy się bezpośrednio z Krajowym Rejestrem Sądowym (KRS) i pobiera
                                aktualne dane rejestrowe spółki: status prawny, siedzibę, numer REGON oraz
                                skład organu uprawnionego do reprezentacji.
                            </p>
                        </div>
                        <div>
                            <h3 className="font-semibold text-primary mb-1">Weryfikacja reprezentantów</h3>
                            <p>
                                Sprawdź, kto jest uprawniony do podpisywania umów w imieniu kontrahenta –
                                członkowie zarządu, prokurenci i likwidatorzy widoczni od razu, bez logowania.
                                Pełna weryfikacja tożsamości dostępna po rejestracji.
                            </p>
                        </div>
                        <div>
                            <h3 className="font-semibold text-primary mb-1">Bezpieczna współpraca</h3>
                            <p>
                                Weryfikacja kontrahenta przed podpisaniem umowy to podstawa bezpiecznego
                                biznesu. Sprawdź NIP spółki zanim zaciągniesz zobowiązanie – sprawdź
                                bezpłatnie. Prosty raport zamiast skomplikowanych zapisów.
                            </p>
                        </div>
                    </div>
                </section>

            </div>
        </div>
    );
};

export default QuickReportPage;
